import { Image, Link, Typography } from 'beek-design-system';
import { useState } from 'react';
import { useTranslations } from 'next-intl';
import styled from 'styled-components';

export type MoreToLearnType = {
  title: string;
  author: string;
  cover_url: string;
  slug: string;
};

type WrapperProps = {
  index: number;
};

type CardContainerProps = {
  hover: boolean;
};

const CoversGrid = styled.ul`
  display: grid;
  width: fit-content;
  grid-template-columns: repeat(2, 8.75rem);
  grid-gap: 2.5rem;
  box-sizing: border-box;
  @media ${({ theme }): string => theme.devices.laptop} {
    grid-template-columns: repeat(5, 8.75rem);
    grid-gap: 3.375rem;
  }
`;

const CoverWrapper = styled.div<WrapperProps>`
  align-self: center;
  justify-content: center;
  margin-top: ${(props): string => (props.index >= 2 ? '0.75rem' : '2rem')};
  @media ${({ theme }): string => theme.devices.laptop} {
    margin-top: ${(props): string => (props.index >= 5 ? '1.875rem' : '3rem')};
    margin-right: 3.375rem;
    width: 8.75rem;
  }
`;

const MainWrapper = styled.div`
  margin-left: 1rem;
  width: fit-content;
  margin: 0 auto 0 auto;
  @media ${({ theme }): string => theme.devices.laptop} {
    width: 57.3125rem;
    height: 36.25rem;
    margin-bottom: 12.5rem;
  }
`;

const CardContainer = styled.li<CardContainerProps>`
  outline: none;
  cursor: pointer;

  p[type='subtitle1'] {
    text-decoration: ${(props): string => (props.hover ? 'underline' : '')};
    text-decoration-color: ${(props): string =>
      props.hover ? 'var(--gray-70)' : ''};
    color: ${(props): string => (props.hover ? 'var(--gray-70)' : '')};
  }
  p[type='subtitle1']:active {
    text-decoration: underline;
    text-decoration-color: var(--gray-100);
    color: var(--gray-100);
  }
`;

const ExtendedTypo = styled(Typography)`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const Cover = (
  title: string,
  author: string,
  url: string,
  slug: string,
  index: number
): JSX.Element => {
  const [hover, setHover] = useState(false);
  return (
    <CardContainer
      onMouseEnter={(): void => setHover(!hover)}
      onMouseLeave={(): void => setHover(!hover)}
      hover={hover}
      key={`Audiobook ${title}-${index}`}
    >
      <Link href={`/libros/${slug}`} title={title} textDecoration="none">
        <CoverWrapper index={index}>
          <Image
            url={url}
            alt="Beek image"
            type="more_to_learn"
            border
            borderRadius
          />
          <ExtendedTypo type="subtitle1" marginTop="1rem">
            {title}
          </ExtendedTypo>
          <Typography type="body2" colorVariant="gray70" marginTop="0.125rem">
            {author}
          </Typography>
        </CoverWrapper>
      </Link>
    </CardContainer>
  );
};

const Covers = (coverObj: MoreToLearnType[]): JSX.Element[] =>
  coverObj.map((book, i) =>
    Cover(book.title, book.author, book.cover_url, book.slug, i)
  );

const MoreToLearn = (books: MoreToLearnType[]): JSX.Element => {
  const t = useTranslations('audioContent');
  const booksData = Object.values(books);
  const limitMoreToLearn = booksData.slice(0, 10);
  return (
    <MainWrapper>
      <Typography data-cy="more-to-learn-title" type="headline3">{`${t(
        'coverGrid'
      )}`}</Typography>
      <CoversGrid>{Covers(limitMoreToLearn)}</CoversGrid>
    </MainWrapper>
  );
};

export default MoreToLearn;
